<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center flex-wrap gap-2 mb-4">
          <div>
            <h1 class="text-h4 mb-0">{{ $t('walletOperations.title') }}</h1>
            <p v-if="documentFilter" class="text-body-2 text-medium-emphasis mt-1 mb-0">
              {{ $t('walletOperations.filteredByDocument', { type: documentTypeLabel, id: documentId }) }}
              <router-link :to="{ path: '/wallet-operations', query: currentQuery }" class="ml-2">
                {{ $t('walletOperations.showAll') }}
              </router-link>
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters (like Transactions) -->
    <v-row>
      <v-col cols="12">
        <div class="mb-4">
          <v-row>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('walletOperations.dateFrom')"
                type="date"
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-text-field
                v-model="filterDateTo"
                :label="$t('walletOperations.dateTo')"
                type="date"
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterWallet"
                :items="wallets"
                item-title="display_name"
                item-value="id"
                :label="$t('walletOperations.walletFilter')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select
                v-model="filterCoin"
                :items="coins"
                item-title="symbol"
                item-value="id"
                :label="$t('walletOperations.coinFilter')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-btn-toggle v-model="datePeriod" mandatory class="period-buttons">
                <v-btn value="day" size="small">{{ $t('walletOperations.day') }}</v-btn>
                <v-btn value="week" size="small">{{ $t('walletOperations.week') }}</v-btn>
                <v-btn value="month" size="small">{{ $t('walletOperations.month') }}</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>

    <!-- Wallet operations table -->
    <v-row>
      <v-col cols="12">
        <v-table density="compact" class="no-border-table">
          <thead>
            <tr>
              <th class="px-4 py-3">{{ $t('walletOperations.operatedAt') }}</th>
              <th class="px-4 py-3">{{ $t('walletOperations.documentType') }}</th>
              <th class="px-4 py-3">{{ $t('walletOperations.documentId') }}</th>
              <th class="px-4 py-3">{{ $t('walletOperations.wallet') }}</th>
              <th class="px-4 py-3 text-right">{{ $t('walletOperations.amount') }}</th>
              <th class="px-4 py-3 text-right">{{ $t('walletOperations.amountUsdt') }}</th>
              <th class="px-4 py-3">{{ $t('walletOperations.operationKind') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="op in operations" :key="op.id">
              <td>{{ formatDateTime(op.operated_at) }}</td>
              <td>{{ documentTypeLabelFor(op.document_type) }}</td>
              <td>
                <router-link v-if="documentLink(op).path !== '#'" :to="documentLink(op)" class="text-primary text-decoration-none">
                  {{ op.document_id }}
                </router-link>
                <span v-else>{{ op.document_id }}</span>
              </td>
              <td>{{ op.wallet_display }}</td>
              <td class="text-right" :class="amountClass(op.amount)">
                {{ formatAmount(op.amount) }}
              </td>
              <td class="text-right" :class="amountClass(op.amount_usdt)">
                {{ formatAmount(op.amount_usdt) }}
              </td>
              <td>{{ operationKindLabel(op.operation_kind) }}</td>
            </tr>
            <tr v-if="operations.length === 0 && !loading">
              <td colspan="7" class="text-center text-grey py-6">
                {{ documentFilter ? $t('walletOperations.noOperationsForDocument') : $t('walletOperations.noOperations') }}
              </td>
            </tr>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-6">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="operations.length > 0 && !loading">
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('walletOperations.totalPositive') }}
              </td>
              <td colspan="1"></td>
              <td class="text-right font-weight-bold text-success pa-3">
                {{ formatAmount(totals.positiveUsdt) }}
              </td>
              <td></td>
            </tr>
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('walletOperations.totalNegative') }}
              </td>
              <td colspan="1"></td>
              <td class="text-right font-weight-bold text-error pa-3">
                {{ formatAmount(totals.negativeUsdt) }}
              </td>
              <td></td>
            </tr>
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('walletOperations.totalNet') }}
              </td>
              <td colspan="1"></td>
              <td class="text-right font-weight-bold pa-3" :class="amountClass(totals.netUsdt)">
                {{ formatAmount(totals.netUsdt) }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </v-table>
      </v-col>
    </v-row>

    <!-- Passive operations for Settlement -->
    <v-row v-if="documentFilter && documentFilter.document_type === 'Settlement'">
      <v-col cols="12">
        <v-divider class="my-4" />
        <h2 class="text-h6 mb-2">{{ $t('passiveOperations.title') }}</h2>
        <v-table density="compact" class="no-border-table">
          <thead>
            <tr>
              <th class="px-4 py-3">{{ $t('passiveOperations.operatedAt') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.investor') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.documentType') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.documentId') }}</th>
              <th class="px-4 py-3 text-right">{{ $t('passiveOperations.amountUsdt') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.operationKind') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="op in passiveOperations" :key="op.id">
              <td>{{ formatDateTime(op.operated_at) }}</td>
              <td>{{ op.investor_name }}</td>
              <td>{{ documentTypeLabelFor(op.document_type) }}</td>
              <td>
                <router-link v-if="documentLink(op).path !== '#'" :to="documentLink(op)" class="text-primary text-decoration-none">
                  {{ op.document_id }}
                </router-link>
                <span v-else>{{ op.document_id }}</span>
              </td>
              <td class="text-right" :class="amountClass(op.amount_usdt)">
                {{ formatAmount(op.amount_usdt) }}
              </td>
              <td>{{ operationKindLabel(op.operation_kind) }}</td>
            </tr>
            <tr v-if="passiveOperations.length === 0 && !passiveLoading">
              <td colspan="6" class="text-center text-grey py-6">
                {{ $t('passiveOperations.noOperationsForDocument') }}
              </td>
            </tr>
            <tr v-if="passiveLoading">
              <td colspan="6" class="text-center py-6">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="passiveOperations.length > 0 && !passiveLoading">
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('passiveOperations.totalPositive') }}
              </td>
              <td class="text-right font-weight-bold text-success pa-3">
                {{ formatAmount(passiveTotals.positiveUsdt) }}
              </td>
              <td></td>
            </tr>
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('passiveOperations.totalNegative') }}
              </td>
              <td class="text-right font-weight-bold text-error pa-3">
                {{ formatAmount(passiveTotals.negativeUsdt) }}
              </td>
              <td></td>
            </tr>
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('passiveOperations.totalNet') }}
              </td>
              <td class="text-right font-weight-bold pa-3" :class="amountClass(passiveTotals.netUsdt)">
                {{ formatAmount(passiveTotals.netUsdt) }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </v-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet } from '../utils/api'
import { getDateRangeForPeriod } from '../utils/date'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

interface Wallet {
  id: number
  display_name?: string
  exchange?: { name: string }
  coin?: { symbol: string }
}

interface Coin {
  id: number
  symbol: string
  name: string
}

interface WalletOperationRow {
  id: number
  operated_at: string
  document_type: string
  document_id: number
  wallet_id: number
  wallet_display: string
  amount: string
  amount_usdt: string
  operation_kind: string | null
}

const operations = ref<WalletOperationRow[]>([])
const loading = ref(false)
interface PassiveOperationRow {
  id: number
  operated_at: string
  document_type: string
  document_id: number
  investor_id: number
  investor_name: string
  amount_usdt: string | number
  operation_kind: string | null
}

const passiveOperations = ref<PassiveOperationRow[]>([])
const passiveLoading = ref(false)
const wallets = ref<Wallet[]>([])
const coins = ref<Coin[]>([])

// Filter state (synced to URL like Transactions)
const filterDateFrom = ref<string | null>(null)
const filterDateTo = ref<string | null>(null)
const filterWallet = ref<number | null>(null)
const filterCoin = ref<number | null>(null)
const datePeriod = ref<string>('day')
// Base date for period when viewing operations for a specific document
const documentBaseDate = ref<string | null>(null)

function documentPath(documentType: string): string {
  switch (documentType) {
    case 'Transaction': return '/transactions'
    case 'Settlement': return '/settlements'
    case 'Expense': return '/expenses'
    case 'CourierTransfer': return '/courier-transfers'
    default: return '#'
  }
}

function documentLink(op: { document_type: string; document_id: number }): { path: string } {
  const base = documentPath(op.document_type)
  if (base === '#') return { path: '#' }
  return { path: `${base}/${op.document_id}` }
}

const documentFilter = computed(() => {
  const type = route.query.document_type as string
  const id = route.query.document_id as string
  return type && id ? { document_type: type, document_id: id } : null
})

const documentTypeLabel = computed(() => {
  if (!documentFilter.value) return ''
  return documentTypeLabelFor(documentFilter.value.document_type)
})

const documentId = computed(() => documentFilter.value?.document_id ?? '')

// Query for "Show all" link (preserve filters, drop document filter)
const currentQuery = computed(() => {
  const q: Record<string, string> = {}
  if (filterDateFrom.value) q.date_from = filterDateFrom.value
  if (filterDateTo.value) q.date_to = filterDateTo.value
  if (filterWallet.value != null) q.wallet = String(filterWallet.value)
  if (filterCoin.value != null) q.coin = String(filterCoin.value)
  q.period = datePeriod.value
  return q
})

// Totals from current operations list (amount_usdt)
const totals = computed(() => {
  let positiveUsdt = 0
  let negativeUsdt = 0
  for (const op of operations.value) {
    const v = parseFloat(String(op.amount_usdt || 0))
    if (!Number.isFinite(v)) continue
    if (v > 0) positiveUsdt += v
    else if (v < 0) negativeUsdt += v
  }
  return {
    positiveUsdt,
    negativeUsdt,
    netUsdt: positiveUsdt + negativeUsdt
  }
})

const passiveTotals = computed(() => {
  let positiveUsdt = 0
  let negativeUsdt = 0
  for (const op of passiveOperations.value) {
    const v = parseFloat(String(op.amount_usdt || 0))
    if (!Number.isFinite(v)) continue
    if (v > 0) positiveUsdt += v
    else if (v < 0) negativeUsdt += v
  }
  return {
    positiveUsdt,
    negativeUsdt,
    netUsdt: positiveUsdt + negativeUsdt
  }
})

function documentTypeLabelFor(type: string): string {
  const key = `walletOperations.docType.${type}`
  const translated = t(key)
  return translated !== key ? translated : type
}

function operationKindLabel(kind: string | null): string {
  if (!kind) return '—'
  const key = `walletOperations.kind.${kind}`
  const translated = t(key)
  return translated !== key ? translated : kind
}

const formatDateTime = (val: string): string => {
  if (!val) return '—'
  const d = new Date(val)
  if (isNaN(d.getTime())) return val
  return d.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short'
  })
}

const formatAmount = (amount: string | number | null | undefined): string => {
  const value = typeof amount === 'number' ? amount : parseFloat(String(amount || 0))
  if (isNaN(value)) return '0.00'
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

const amountClass = (amount: string | number | null | undefined): string => {
  const value = typeof amount === 'number' ? amount : parseFloat(String(amount || 0))
  if (isNaN(value)) return ''
  return value < 0 ? 'text-error' : value > 0 ? 'text-success' : ''
}

function setDatePeriod(period: string, baseDate?: string | null) {
  const range = baseDate
    ? getDateRangeForPeriod(period, 'sunday', baseDate)
    : getDateRangeForPeriod(period, 'sunday')
  filterDateFrom.value = range.from
  filterDateTo.value = range.to
}

function initFromQuery() {
  const q = route.query
  filterDateFrom.value = (q.date_from as string) || null
  filterDateTo.value = (q.date_to as string) || null
  const walletId = q.wallet != null ? parseInt(String(q.wallet), 10) : NaN
  const coinId = q.coin != null ? parseInt(String(q.coin), 10) : NaN
  filterWallet.value = Number.isFinite(walletId) ? walletId : null
  filterCoin.value = Number.isFinite(coinId) ? coinId : null
  const period = (q.period as string) || 'day'
  if (['day', 'week', 'month'].includes(period)) datePeriod.value = period
  // When opening by document link, we'll anchor period to document date later
  if (!q.date_from && !q.date_to && !documentFilter.value) {
    setDatePeriod(datePeriod.value)
  }
}

function syncFiltersToUrl() {
  const query: Record<string, string> = {}
  if (filterDateFrom.value) query.date_from = filterDateFrom.value
  if (filterDateTo.value) query.date_to = filterDateTo.value
  if (filterWallet.value != null) query.wallet = String(filterWallet.value)
  if (filterCoin.value != null) query.coin = String(filterCoin.value)
  query.period = datePeriod.value
  if (documentFilter.value) {
    query.document_type = documentFilter.value.document_type
    query.document_id = String(documentFilter.value.document_id)
  }
  router.replace({ path: route.path, query })
}

const loadWallets = async () => {
  const res = await apiGet('/api/wallets')
  if (res.ok) {
    const data = await res.json()
    wallets.value = Array.isArray(data) ? data : []
  }
}

const loadCoins = async () => {
  const res = await apiGet('/api/coins')
  if (res.ok) {
    const data = await res.json()
    coins.value = Array.isArray(data) ? data : []
  }
}

const loadOperations = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (documentFilter.value) {
      params.set('document_type', documentFilter.value.document_type)
      params.set('document_id', documentFilter.value.document_id)
    }
    if (filterWallet.value != null) params.set('wallet_id', String(filterWallet.value))
    if (filterCoin.value != null) params.set('coin_id', String(filterCoin.value))
    if (filterDateFrom.value) params.set('date_from', filterDateFrom.value)
    if (filterDateTo.value) params.set('date_to', filterDateTo.value)
    const url = params.toString() ? `/api/wallet-operations?${params}` : '/api/wallet-operations'
    const res = await apiGet(url)
    if (res.ok) {
      const data = await res.json()
      operations.value = Array.isArray(data) ? data : []
      // If we opened by document and no explicit dates yet, anchor period to document date
      if (
        documentFilter.value &&
        !filterDateFrom.value &&
        !filterDateTo.value &&
        !documentBaseDate.value &&
        operations.value.length > 0
      ) {
        const first = operations.value[0]
        const operatedAt = first.operated_at
        if (operatedAt) {
          const baseDate = String(operatedAt).split('T')[0]
          documentBaseDate.value = baseDate
          setDatePeriod(datePeriod.value, baseDate)
          syncFiltersToUrl()
        }
      }
    } else {
      operations.value = []
    }
  } catch {
    operations.value = []
  } finally {
    loading.value = false
  }
}

const loadPassiveOperations = async () => {
  if (!documentFilter.value || documentFilter.value.document_type !== 'Settlement') {
    passiveOperations.value = []
    return
  }
  passiveLoading.value = true
  try {
    const params = new URLSearchParams()
    params.set('document_type', documentFilter.value.document_type)
    params.set('document_id', documentFilter.value.document_id)
    if (filterDateFrom.value) params.set('date_from', filterDateFrom.value)
    if (filterDateTo.value) params.set('date_to', filterDateTo.value)
    const url = `/api/passive-operations?${params.toString()}`
    const res = await apiGet(url)
    if (res.ok) {
      const data = await res.json()
      passiveOperations.value = Array.isArray(data) ? data : []
      // Fallback: if there were no wallet operations, anchor period to passive op date
      if (
        documentFilter.value &&
        !filterDateFrom.value &&
        !filterDateTo.value &&
        !documentBaseDate.value &&
        passiveOperations.value.length > 0
      ) {
        const first = passiveOperations.value[0]
        const operatedAt = first.operated_at
        if (operatedAt) {
          const baseDate = String(operatedAt).split('T')[0]
          documentBaseDate.value = baseDate
          setDatePeriod(datePeriod.value, baseDate)
          syncFiltersToUrl()
        }
      }
    } else {
      passiveOperations.value = []
    }
  } catch {
    passiveOperations.value = []
  } finally {
    passiveLoading.value = false
  }
}

watch(datePeriod, (period) => {
  if (documentFilter.value && documentBaseDate.value) {
    setDatePeriod(period, documentBaseDate.value)
  } else {
    setDatePeriod(period)
  }
})
watch([filterDateFrom, filterDateTo, filterWallet, filterCoin], syncFiltersToUrl, { deep: true })

onMounted(async () => {
  initFromQuery()
  syncFiltersToUrl()
  await Promise.all([loadWallets(), loadCoins()])
  await loadOperations()
  await loadPassiveOperations()
})
watch(() => [route.query.document_type, route.query.document_id, route.query.date_from, route.query.date_to, route.query.wallet, route.query.coin, route.query.period], () => {
  initFromQuery()
  loadOperations()
  loadPassiveOperations()
})
</script>
